import { EngineService } from './engine.service';
import { Observable, of, concat } from 'rxjs';
import { Store } from '@ngxs/store';
import { ToggleLabels } from './state/engine.actions';
import { ImageService } from '../shared/image.service';
import { flatMap, map, combineAll, catchError } from 'rxjs/operators';
import { LabelsService } from '../story/labels.service';
import * as i0 from "@angular/core";
import * as i1 from "./engine.service";
import * as i2 from "../shared/image.service";
import * as i3 from "../story/labels.service";
import * as i4 from "@ngxs/store";
export class SnapshotService {
    constructor(engineService, imageService, labelsService, store) {
        this.engineService = engineService;
        this.imageService = imageService;
        this.labelsService = labelsService;
        this.store = store;
    }
    localResize(width, height) {
        /*
        Set the size of the container with css
        Set the size of the canvas drawingbuffers via canvas width/height properties
        */
        const engineCanvas = Human.renderer.canvas.canvas;
        const annotationCanvas = Human.renderer.canvas.annotationCanvas;
        const container = engineCanvas.parentNode;
        // original dimensions (container == 3D canvas == 2D canvas)
        const originalWidth = container.clientWidth;
        const originalHeight = container.clientHeight;
        // set width on container and canvas
        container.width = width;
        container.height = height;
        engineCanvas.width = width;
        engineCanvas.height = height;
        annotationCanvas.width = width;
        annotationCanvas.height = height;
        // style the container to match the element dimensions
        container.style.width = `${width}px`;
        container.style.height = `${height}px`;
        // Update the viewport
        Human.renderer.canvas.init(); // or HACK set engine's canvas.width/height manually
        Human.renderer.viewportDirty = true;
        Human.renderer.camera.updateCanvas();
        Human.renderer.postprocess.updateCanvas();
        Human.renderer.draw.updateCanvas();
        Human.renderer.viewportDirty = true;
        Human.renderer.prepareFrame();
        Human.renderer.forceRenderFrame();
        return [originalWidth, originalHeight];
    }
    // snapshot when requested dimension fits inside of 3D canvas
    drawHumanCanvasToThumbnail(thumbnailCanvas) {
        // calculate source width and height dimensions
        const thumbanilAspect = thumbnailCanvas.width / thumbnailCanvas.height;
        const humanCanvas = this.engineService.getCanvas();
        const sourceHeight = humanCanvas.height;
        const sourceWidth = thumbanilAspect * sourceHeight;
        // calculate source offset
        const offsetX = 0.5 * (humanCanvas.width - sourceWidth);
        // render 3D frame and transfer to thumbnail canvas
        return new Observable((observer) => {
            const thumbnailContext = thumbnailCanvas.getContext('2d');
            Human.renderer.forceRenderFrame();
            thumbnailContext.drawImage(humanCanvas, offsetX, 0, sourceWidth, sourceHeight, 0, 0, thumbnailCanvas.width, thumbnailCanvas.height);
            observer.next();
            observer.complete();
        });
    }
    // snapshot when requested dimension does NOT fit inside of 3D canvas (avoid pixelation)
    drawResizeHumanCanvasToThumbnail(thumbnailCanvas) {
        const humanCanvas = this.engineService.getCanvas();
        humanCanvas.style.opacity = '0'; // hide while we do our resizing business. start of flash
        this.store.dispatch(new ToggleLabels(false));
        // resize 3D canvas
        const [originalWidth, originalHeight] = this.localResize(thumbnailCanvas.width, thumbnailCanvas.height);
        const thumbnailContext = thumbnailCanvas.getContext('2d');
        return new Observable((observer) => {
            setTimeout(() => {
                // force webgl render frame
                Human.renderer.viewportDirty = true;
                Human.renderer.prepareFrame();
                Human.renderer.forceRenderFrame();
                // draw webgl canvas to 2d canvas image
                thumbnailContext.drawImage(humanCanvas, 0, 0, humanCanvas.width, humanCanvas.height);
                // return the original size
                this.localResize(originalWidth, originalHeight);
                document.getElementById('theCanvas').style.opacity = '';
                this.store.dispatch(new ToggleLabels(true));
                observer.next();
                observer.complete();
            }, 10);
        });
    }
    drawRadialGradient(thumbnailCanvas, background) {
        // convert to RGB colors
        const colors255 = background.colors;
        // convert stop percentages into radius values on the canvas
        const toRadius = (percentage, width, height) => {
            const [x2, y2] = [Math.pow(width / 2, 2), Math.pow(height / 2, 2)];
            return percentage / 100 * Math.pow(x2 + y2, 0.5);
        };
        const [radiusInner, radiusOuter] = background.colorStops.map((s) => toRadius(s, thumbnailCanvas.width, thumbnailCanvas.height));
        // create gradient, fillstyle
        const thumbnailContext = thumbnailCanvas.getContext('2d');
        const [x0, y0] = [thumbnailCanvas.width / 2, thumbnailCanvas.height / 2];
        const gradient = thumbnailContext.createRadialGradient(x0, y0, radiusInner, x0, y0, radiusOuter);
        gradient.addColorStop(background.colorStops[0] / 100.0, `rgba(${colors255.slice(0, 4).join(',')})`);
        gradient.addColorStop(background.colorStops[1] / 100.0, `rgba(${colors255.slice(4, 8).join(',')})`);
        // draw gradient
        thumbnailContext.fillStyle = gradient;
        thumbnailContext.fillRect(0, 0, thumbnailCanvas.width, thumbnailCanvas.height);
    }
    drawLinearGradient(thumbnailCanvas, background) {
        // convert to RGB colors
        const colors255 = background.colors;
        // create gradient, fillstyle
        const thumbnailContext = thumbnailCanvas.getContext('2d');
        const [x0, y0] = [thumbnailCanvas.width / 2, thumbnailCanvas.height / 2];
        const gradient = thumbnailContext.createLinearGradient(x0, y0, x0, thumbnailCanvas.height);
        gradient.addColorStop(background.colorStops[0] / 100.0, `rgba(${colors255.slice(0, 4).join(',')})`);
        gradient.addColorStop(background.colorStops[1] / 100.0, `rgba(${colors255.slice(4, 8).join(',')})`);
        // draw gradient
        thumbnailContext.fillStyle = gradient;
        thumbnailContext.fillRect(0, 0, thumbnailCanvas.width, thumbnailCanvas.height);
    }
    // takeSnapshot of canvas return an observable
    takeSnapshot(width, height, background, includeLabels, snapshotConfig) {
        /*
        Create 2D canvas to serve as the thumbnail
        Copy background data to thumbnail canvas
        Copy 3D canvas to thumbnail canvas
        */
        // config
        const { imageType, imageQuality, copyrightEnabled } = snapshotConfig;
        // Create 2D canvas on which to draw 3D context
        const thumbnailCanvas = document.createElement('canvas');
        thumbnailCanvas.width = width;
        thumbnailCanvas.height = height;
        // Draw Background onto thumbnail canvas
        if (background.gradientType === 'radial') {
            this.drawRadialGradient(thumbnailCanvas, background);
        }
        else if (background.gradientType === 'linear') {
            this.drawLinearGradient(thumbnailCanvas, background);
        }
        const engineCanvas = this.engineService.getCanvas();
        const canvasResize = thumbnailCanvas.height > engineCanvas.height || thumbnailCanvas.width > engineCanvas.width;
        // composite a list of canvas elements to single image
        const canvases$ = [];
        // Draw 3D canvas
        const drawScene$ = (canvasResize ? this.drawResizeHumanCanvasToThumbnail(thumbnailCanvas) : this.drawHumanCanvasToThumbnail(thumbnailCanvas))
            .pipe(map(() => thumbnailCanvas));
        canvases$.push(drawScene$);
        // Draw labels: optional
        const labelContainer = this.labelsService.getLabelContainer();
        if (includeLabels && labelContainer) {
            const labelCanvas$ = this.imageService.elementToCanvas(labelContainer.nativeElement, true) // render labels
                .pipe(flatMap((elementCanvas) => this.imageService.fitCanvas(elementCanvas, { width, height })) // scale to target
            );
            canvases$.push(labelCanvas$);
        }
        // sequentially render + composite canvas elements
        return concat(canvases$)
            .pipe(
        // gather canvas elements
        combineAll(), 
        // merge canvas elements
        map((canvases) => this.imageService.combineCanvasElements(canvases)), 
        // add logo: optional
        flatMap((canvas) => copyrightEnabled ? this.writeLogoToCanvas(canvas, snapshotConfig) : of(canvas)), 
        // to data blob
        flatMap((compositeCanvas) => this.imageService.canvasToBlob(compositeCanvas, imageType, imageQuality)));
    }
    writeLogoToCanvas(canvas, config) {
        return this.getLogoImage(config.copyrightImagePath) // get logo
            .pipe(
        // write image to canvas
        map((image) => {
            // calculate position
            const { xOffset, yOffset } = this.getLogoPosition(canvas.width, canvas.height, image.width, image.height, config);
            // write image
            const canvasContext = canvas.getContext('2d');
            canvasContext.drawImage(image, xOffset, yOffset);
            return canvas;
        }), 
        // handle errors
        catchError(() => of(canvas)));
    }
    getLogoImage(path) {
        return new Observable((observer) => {
            const image = new Image();
            image.onload = () => {
                observer.next(image);
                observer.complete();
            };
            image.onerror = () => observer.error();
            image.src = path;
        });
    }
    getLogoPosition(viewWidth, viewHeight, imageWidth, imageHeight, config) {
        const { copyrightPosition, copyrightOffset } = config;
        let xPosition;
        let yPosition;
        // Calc X
        switch (copyrightPosition.x) {
            case 'right':
                xPosition = viewWidth - imageWidth;
                break;
            case 'center':
                xPosition = (viewWidth / 2) - (imageWidth / 2);
                break;
            default:
                xPosition = 0;
                break;
        }
        // Calc Y
        switch (copyrightPosition.y) {
            case 'bottom':
                yPosition = viewHeight - imageHeight;
                break;
            case 'center':
                yPosition = (viewHeight / 2) - (imageHeight / 2);
                break;
            default:
                yPosition = 0;
                break;
        }
        // offset w/ config
        return {
            xOffset: xPosition + copyrightOffset.x,
            yOffset: yPosition + copyrightOffset.y
        };
    }
}
SnapshotService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function SnapshotService_Factory() { return new SnapshotService(i0.ɵɵinject(i1.EngineService), i0.ɵɵinject(i2.ImageService), i0.ɵɵinject(i3.LabelsService), i0.ɵɵinject(i4.Store)); }, token: SnapshotService, providedIn: "root" });
